import React, { useEffect, useRef, useState } from "react";
import { UploadCloud, Download, Trash2, FileText, CheckCircle2, RotateCcw, History, Loader2, Package, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { STATUS } from "./shared";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const fmtDate = (s) => (s ? new Date(s).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);

const RequestWorkflowPanel = ({ request, onChanged, isClient = false }) => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [audit, setAudit] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [notify, setNotify] = useState(true);
  const [showResubmit, setShowResubmit] = useState(false);
  const [busy, setBusy] = useState(false);

  const taskId = request?.tasks?.[0]?.id;
  const files = (request?.tasks || []).flatMap((t) => (t.files || []).map((f) => ({ ...f, task_id: t.id })));
  const mySide = isClient ? "client" : "firm";

  const loadAudit = async () => {
    try { const { data } = await api.get(`/requests/${request.id}/audit`); setAudit(data.audit || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadAudit(); /* eslint-disable-next-line */ }, [request?.id, request?.status]);

  const doUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    if (!taskId) { toast({ title: "No upload slot", variant: "destructive" }); return; }
    setUploading(true); setProgress(0);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("note", note || "");
        await api.post(`/requests/${request.id}/tasks/${taskId}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
        });
      }
      setNote("");
      toast({ title: "Uploaded", description: `${fileList.length} file(s) added.` });
      onChanged && (await onChanged());
      loadAudit();
    } catch (err) { toast({ title: "Upload failed", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
    finally { setUploading(false); setProgress(0); }
  };

  const download = async (fid, filename) => {
    try {
      const { data } = await api.get(`/requests/${request.id}/files/${fid}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch { toast({ title: "Download failed", variant: "destructive" }); }
  };

  const downloadZip = async () => {
    try {
      const { data } = await api.get(`/requests/${request.id}/download-zip`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `${request.title || "request"}.zip`; a.click(); URL.revokeObjectURL(url);
    } catch { toast({ title: "No files to download", variant: "destructive" }); }
  };

  const delFile = async (fid) => {
    try { await api.delete(`/requests/${request.id}/files/${fid}`); toast({ title: "File deleted" }); onChanged && (await onChanged()); loadAudit(); }
    catch (err) { toast({ title: "Delete failed", description: err?.response?.data?.detail || "You can only delete your own files.", variant: "destructive" }); }
  };

  const review = async (action) => {
    setBusy(true);
    try {
      await api.post(`/requests/${request.id}/review`, { action, feedback, notify });
      toast({ title: action === "accept" ? "Accepted & Closed" : "Resubmission requested", description: action === "accept" ? "Request closed." : (notify ? "Client notified." : "Status updated.") });
      setShowResubmit(false); setFeedback("");
      onChanged && (await onChanged()); loadAudit();
    } catch (err) { toast({ title: "Action failed", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Left: description + upload + files */}
      <div className="space-y-5 lg:col-span-2">
        {request.description && (
          <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">{request.description}</div>
        )}

        {/* Drag & drop */}
        {request.status !== STATUS.CLOSED && (
          <div>
            <div
              data-testid="dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${dragOver ? "border-[#2f2b8f] bg-indigo-50 dark:bg-indigo-500/10" : "border-slate-200 hover:border-slate-300 dark:border-slate-700"}`}
            >
              <UploadCloud className="h-7 w-7 text-slate-400" />
              <p className="mt-2 text-sm font-medium text-[#1a1a2e] dark:text-slate-200">Drag & drop files here, or click to browse</p>
              <p className="text-xs text-slate-400">Up to 10MB per file</p>
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="file-input" onChange={(e) => doUpload(e.target.files)} />
            </div>
            <Input data-testid="upload-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note for this upload…" className="mt-2 h-9 rounded-xl text-sm" />
            {uploading && <Progress value={progress} className="mt-2 h-2" />}
          </div>
        )}

        {/* Files */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="flex items-center gap-1.5 text-sm font-semibold text-[#1a1a2e] dark:text-slate-200"><FileText className="h-4 w-4" /> Files ({files.length})</h4>
            {files.length > 0 && <Button data-testid="bulk-zip" onClick={downloadZip} size="sm" variant="outline" className="h-8 rounded-lg text-xs"><Package className="mr-1 h-3.5 w-3.5" /> Download all (ZIP)</Button>}
          </div>
          {files.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400 dark:border-slate-700">No files uploaded yet.</p>
          ) : (
            <div className="space-y-2">
              {files.map((f) => (
                <div key={f.id} data-testid={`file-${f.id}`} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2.5 dark:border-slate-800 dark:bg-slate-900">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-[#1a1a2e] dark:text-slate-200">{f.filename}</span>
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${f.side === "client" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"}`}>{f.side === "client" ? "Client" : "Firm"}</span>
                    </div>
                    <div className="text-[11px] text-slate-400">Uploaded by {f.uploaded_by} · {fmtDate(f.uploaded_at)} · {fmtSize(f.size)}{f.note ? ` · "${f.note}"` : ""}</div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button data-testid={`download-${f.id}`} onClick={() => download(f.id, f.filename)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Download className="h-4 w-4" /></button>
                    {(f.side || "client") === mySide && (
                      <button data-testid={`delete-file-${f.id}`} onClick={() => delFile(f.id)} className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Firm actions */}
        {!isClient && request.status !== STATUS.CLOSED && (
          <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/40">
            <h4 className="text-sm font-semibold text-[#1a1a2e] dark:text-slate-200">Review</h4>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button data-testid="accept-close-btn" onClick={() => review("accept")} disabled={busy} size="sm" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />} Accept & Close
              </Button>
              <Button data-testid="resubmit-toggle-btn" onClick={() => setShowResubmit((v) => !v)} disabled={busy} size="sm" variant="outline" className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-500/30 dark:text-amber-300">
                <RotateCcw className="mr-1 h-4 w-4" /> Request Resubmission
              </Button>
            </div>
            {showResubmit && (
              <div className="mt-3 space-y-2">
                <Textarea data-testid="resubmit-feedback" value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="What needs to change?" className="rounded-xl text-sm" rows={3} />
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <Checkbox data-testid="notify-client-check" checked={notify} onCheckedChange={(v) => setNotify(!!v)} /> Notify client by email
                </label>
                <Button data-testid="confirm-resubmit-btn" onClick={() => review("reject")} disabled={busy} size="sm" className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]">Send resubmission request</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right: audit log */}
      <div>
        <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[#1a1a2e] dark:text-slate-200"><History className="h-4 w-4" /> {isClient ? "Activity" : "Audit log"}</h4>
        <div className="max-h-80 space-y-3 overflow-y-auto rounded-xl border border-slate-100 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          {audit.length === 0 ? <p className="text-xs text-slate-400">No activity yet.</p> : audit.map((a) => (
            <div key={a.id} data-testid="audit-entry" className="border-l-2 border-slate-200 pl-3 dark:border-slate-700">
              <div className="flex items-center gap-1.5 text-xs font-medium text-[#1a1a2e] dark:text-slate-200">
                {a.detail || a.action}
                {!isClient && ["client", "both"].includes(a.side) && <ShieldCheck className="h-3 w-3 text-emerald-500" title="Visible to client" />}
              </div>
              <div className="text-[11px] text-slate-400">{a.actor} · {fmtDate(a.timestamp)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RequestWorkflowPanel;
